/**
 * The trace and the timings of one variant (surfaces.md §3.5, §2.3).
 *
 * `[t_ms, code, key, slot, op, x?]`, one entry per keystroke, click and
 * surface step, refused keys included. `t_ms` is from `performance.now()`
 * since this variant was shown, so it is monotonic and immune to her wall
 * clock. The first entry is always `[0, "", "", "", "shown"]`.
 *
 * `timing.hidden_ms` is the time the tab was hidden while this item was up,
 * so a latency measured across a closed lid can be corrected by the engine.
 */
import type { Entry } from "./surfaces/slots";
import type { Timing, TraceEntry } from "./api";

/** The server keeps a trace whole; past this many entries the client stops
 *  appending and says how many it dropped (`trace_truncated`). A child does
 *  not type ten thousand keys into one answer, a stuck key does. */
export const TRACE_MAX = 4000;

export interface Clock {
  now(): number; // performance.now()
  wall(): number; // Date.now()
  hidden(): boolean;
}

export const browserClock: Clock = {
  now: () => performance.now(),
  wall: () => Date.now(),
  hidden: () => typeof document !== "undefined" && document.visibilityState === "hidden",
};

export class Recorder {
  readonly shownAt: number;
  private t0: number;
  private entries: TraceEntry[] = [[0, "", "", "", "shown"]];
  private dropped = 0;
  private firstKey: number | null = null;
  private hiddenMs = 0;
  private hiddenSince: number | null = null;

  constructor(private clock: Clock = browserClock) {
    this.t0 = clock.now();
    this.shownAt = clock.wall();
    if (clock.hidden()) this.hiddenSince = this.t0;
  }

  elapsed(): number {
    return Math.max(0, Math.round(this.clock.now() - this.t0));
  }

  push(e: Entry, keystroke = true): void {
    const t = this.elapsed();
    if (keystroke && this.firstKey === null && e[0] !== "") this.firstKey = t;
    if (this.entries.length >= TRACE_MAX) {
      this.dropped += 1;
      return;
    }
    this.entries.push([t, ...e] as TraceEntry);
  }

  /** Page Visibility, for `hidden_ms`; the blur and focus themselves are traced too. */
  visibility(hidden: boolean): void {
    const now = this.clock.now();
    if (hidden && this.hiddenSince === null) {
      this.hiddenSince = now;
      this.push(["", "", "", "blur"], false);
    } else if (!hidden && this.hiddenSince !== null) {
      this.hiddenMs += now - this.hiddenSince;
      this.hiddenSince = null;
      this.push(["", "", "", "focus"], false);
    }
  }

  hidden(): number {
    const open = this.hiddenSince !== null ? this.clock.now() - this.hiddenSince : 0;
    return Math.round(this.hiddenMs + open);
  }

  trace(): TraceEntry[] {
    return this.entries.map((e) => [...e] as TraceEntry);
  }

  truncated(): number | undefined {
    return this.dropped > 0 ? this.dropped : undefined;
  }

  timing(): Timing {
    return { shown_at: this.shownAt, first_key_ms: this.firstKey, submit_ms: this.elapsed(), hidden_ms: this.hidden() };
  }

  firstKeyMs(): number | null {
    return this.firstKey;
  }
}
